import { useMutation, useQuery, useQueryClient } from 'react-query';
import QUERY_KEYS from '../../../Lib/Utils/Const/queries-keys';
import {
  CounterParams,
  CounterQuoteRequest,
  CounterSampleShippedRequest,
  PrevSampleReceivedRequest,
  UpdateContactRequest,
} from '../types';
import CountersAPI from '../../../Lib/Api/counters';

export const useCounter = (userId: number, params: CounterParams) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_COUNTER, userId, params],
    queryFn: () => CountersAPI.getVendorCounters(userId, params),
  });

export const useCounterChat = (userId: number | string, counterId: string) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_COUNTER_CHAT, userId, counterId],
    queryFn: () => CountersAPI.getCounterChat(userId, counterId),
    refetchInterval: 60000,
  });

export const useCounterActivities = (
  userId: number | string,
  counterId: string,
) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_COUNTER_ACTIVITIES, userId, counterId],
    queryFn: () => CountersAPI.getCounterActivities(userId, counterId),
    refetchInterval: 30000,
  });

export const useEditQuote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      counterId,
      data,
    }: {
      userId: number;
      counterId: string;
      data: CounterQuoteRequest;
    }) => CountersAPI.editQuote(userId, counterId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(QUERY_KEYS.GET_COUNTER);
    },
  });
};

export const useMarkPrevSampleAsReceived = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      counterId,
      data,
    }: {
      userId: number;
      counterId: string;
      data: PrevSampleReceivedRequest;
    }) => CountersAPI.markPrevSampleAsReceived(userId, counterId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(QUERY_KEYS.GET_COUNTER);
    },
  });
};

export const useMarkCounterSampleAsShipped = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      counterId,
      userId,
      data,
    }: {
      userId: number;
      counterId: string;
      data: CounterSampleShippedRequest;
    }) => CountersAPI.markCounterSampleAsShipped(userId, counterId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(QUERY_KEYS.GET_COUNTER);
    },
  });
};

export const useUpdateContacts = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      counterId,
      data,
    }: {
      userId: number;
      counterId: string;
      data: UpdateContactRequest;
    }) => CountersAPI.updateContacts(userId, counterId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(QUERY_KEYS.GET_COUNTER);
    },
  });
};
